import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Calculator, Heart, Activity } from 'lucide-react';
const Index = () => {
  // Herzfrequenz-Berechnung State
  const [speed, setSpeed] = useState<string>('50');
  const [method, setMethod] = useState<string>('large');
  const [boxes, setBoxes] = useState<string>('');
  const [heartRate, setHeartRate] = useState<number | null>(null);

  // QTc-Berechnung State
  const [qtInterval, setQtInterval] = useState<string>('');
  const [qtUnit, setQtUnit] = useState<string>('ms');
  const [rrInterval, setRrInterval] = useState<string>('');
  const [useHeartRate, setUseHeartRate] = useState<boolean>(false);
  const [hrForQtc, setHrForQtc] = useState<string>('');
  const [qtcBazett, setQtcBazett] = useState<number | null>(null);
  const [qtcFridericia, setQtcFridericia] = useState<number | null>(null);

  // Herzfrequenz berechnen
  const calculateHeartRate = () => {
    const boxCount = parseFloat(boxes);
    if (isNaN(boxCount) || boxCount <= 0) {
      setHeartRate(null);
      return;
    }
    let hr: number;
    if (speed === '50') {
      if (method === 'large') {
        hr = 600 / boxCount;
      } else {
        hr = 1500 / boxCount;
      }
    } else {
      // 25 mm/s
      hr = 300 / boxCount;
    }
    setHeartRate(Math.round(hr));
  };

  // QTc berechnen
  const calculateQTc = () => {
    let qtInSeconds = parseFloat(qtInterval);
    if (isNaN(qtInSeconds) || qtInSeconds <= 0) {
      setQtcBazett(null);
      setQtcFridericia(null);
      return;
    }

    // QT in Sekunden umwandeln falls in ms eingegeben
    if (qtUnit === 'ms') {
      qtInSeconds = qtInSeconds / 1000;
    }
    let rrInSeconds: number;
    if (useHeartRate) {
      const hr = parseFloat(hrForQtc);
      if (isNaN(hr) || hr <= 0) {
        setQtcBazett(null);
        setQtcFridericia(null);
        return;
      }
      rrInSeconds = 60 / hr;
    } else {
      rrInSeconds = parseFloat(rrInterval);
      if (isNaN(rrInSeconds) || rrInSeconds <= 0) {
        setQtcBazett(null);
        setQtcFridericia(null);
        return;
      }
    }

    // Bazett: QTc = QT / √RR
    const bazett = qtInSeconds / Math.sqrt(rrInSeconds);

    // Fridericia: QTc = QT / RR^(1/3)
    const fridericia = qtInSeconds / Math.pow(rrInSeconds, 1 / 3);
    setQtcBazett(Math.round(bazett * 1000)); // in ms
    setQtcFridericia(Math.round(fridericia * 1000)); // in ms
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center py-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Activity className="h-8 w-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">EKG-Parameter Rechner</h1>
          </div>
          <p className="text-lg text-gray-600">Berechnung von Herzfrequenz und QTc-Intervall</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Herzfrequenz-Berechnung */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Herzfrequenz-Berechnung
              </CardTitle>
              <CardDescription className="text-red-50">
                Berechnung aus EKG-Kästchenabständen
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="speed">Schreibgeschwindigkeit</Label>
                <Select value={speed} onValueChange={setSpeed}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25 mm/s</SelectItem>
                    <SelectItem value="50">50 mm/s</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Methode</Label>
                <RadioGroup value={method} onValueChange={setMethod}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="large" id="large" />
                    <Label htmlFor="large">Anzahl großer Kästchen zwischen zwei R-Zacken</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="small" id="small" />
                    <Label htmlFor="small">Anzahl kleiner Kästchen zwischen zwei R-Zacken</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="boxes">
                  Anzahl {method === 'large' ? 'großer' : 'kleiner'} Kästchen
                </Label>
                <Input id="boxes" type="number" step="0.1" value={boxes} onChange={e => setBoxes(e.target.value)} placeholder="z.B. 5.2" />
              </div>

              <Button onClick={calculateHeartRate} className="w-full" size="lg">
                <Calculator className="h-4 w-4 mr-2" />
                Herzfrequenz berechnen
              </Button>

              {heartRate !== null && <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-lg font-semibold text-green-800">
                    Herzfrequenz: {heartRate} bpm
                  </p>
                </div>}
            </CardContent>
          </Card>

          {/* QTc-Berechnung */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                QTc-Berechnung
              </CardTitle>
              <CardDescription className="text-blue-50">
                Frequenzkorrigiertes QT-Intervall
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label htmlFor="qt">QT-Intervall</Label>
                  <Input id="qt" type="number" step="0.1" value={qtInterval} onChange={e => setQtInterval(e.target.value)} placeholder="z.B. 400" />
                </div>
                <div>
                  <Label htmlFor="qtUnit">Einheit</Label>
                  <Select value={qtUnit} onValueChange={setQtUnit}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ms">ms</SelectItem>
                      <SelectItem value="s">s</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label>RR-Intervall bestimmen</Label>
                <RadioGroup value={useHeartRate ? 'hr' : 'rr'} onValueChange={value => setUseHeartRate(value === 'hr')}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="rr" id="rr" />
                    <Label htmlFor="rr">RR-Intervall direkt eingeben (s)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="hr" id="hr" />
                    <Label htmlFor="hr">Aus Herzfrequenz berechnen</Label>
                  </div>
                </RadioGroup>
              </div>

              {useHeartRate ? <div className="space-y-2">
                  <Label htmlFor="hrForQtc">Herzfrequenz (bpm)</Label>
                  <Input id="hrForQtc" type="number" value={hrForQtc} onChange={e => setHrForQtc(e.target.value)} placeholder="z.B. 75" />
                </div> : <div className="space-y-2">
                  <Label htmlFor="rr">RR-Intervall (s)</Label>
                  <Input id="rr" type="number" step="0.01" value={rrInterval} onChange={e => setRrInterval(e.target.value)} placeholder="z.B. 0.8" />
                </div>}

              <Button onClick={calculateQTc} className="w-full" size="lg">
                <Calculator className="h-4 w-4 mr-2" />
                QTc berechnen
              </Button>

              {qtcBazett !== null && qtcFridericia !== null && <div className="mt-4 space-y-3">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-semibold text-blue-800">
                      QTc (Bazett): {qtcBazett} ms
                    </p>
                  </div>
                  <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <p className="font-semibold text-cyan-800">
                      QTc (Fridericia): {qtcFridericia} ms
                    </p>
                  </div>
                  
                  <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <p className="font-medium mb-1">Hinweise:</p>
                    <p>• <strong>Bazett-Formel:</strong> Standard, kann bei hohen HF ungenau sein</p>
                    <p>• <strong>Fridericia-Formel:</strong> genauer bei HF &gt; 80 bpm</p>
                  </div>
                </div>}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center py-4 text-sm text-gray-500">
          <p>Für medizinisches Fachpersonal • Alle Berechnungen erfolgen lokal im Browser</p>
        </div>
      </div>
    </div>;
};
export default Index;